import { memo, useRef, useState } from 'react';
import { StyleSheet, Image, ScrollView, TouchableOpacity, View } from 'react-native';
import { AVPlaybackSourceObject, ResizeMode, Video } from 'expo-av';
import { DeviceType } from 'expo-device';

import { spacing, useDeviceType } from '@pletly/ui';
import RemoveIcon from '%assets/icons/remove-image-close.svg';
import { useMediaSource } from '%components/MediaSlider/Media/MediaVideo/MediaPlayer/useMediaSource';
import { useAuthHeaders } from '%hooks/auth';
import { Props } from './types';
import { AddNewButton } from '../AddNewButton';

export const ImagePickerCollage = ({
	images,
	onAdd,
	onRemove,
	disabledNew,
	buttonStyle,
	style,
	actions,
}: Props) => {
	const [scrollWidth, setScrollWidth] = useState(0);
	const scrollRef = useRef<ScrollView>(null);
	const deviceType = useDeviceType();
	const { getHeaders } = useAuthHeaders();

	if (deviceType === 'loading') {
		return null;
	}

	if (images.length === 0) {
		return <AddNewButton style={[buttonStyle, style]} onPress={onAdd} disabled={disabledNew} />;
	}

	return (
		<View style={[styles.root, style]}>
			<ScrollView
				horizontal
				ref={scrollRef}
				onContentSizeChange={(contentWidth) => {
					if (contentWidth > scrollWidth) {
						scrollRef.current?.scrollToEnd({
							animated: true,
						});
					}

					setScrollWidth(contentWidth);
				}}
				contentContainerStyle={styles.imagesContainer}
			>
				{images.map((image) => (
					<TouchableOpacity key={images.indexOf(image)} activeOpacity={1}>
						{image.type === 'image' ? (
							<Image
								source={{ uri: image.uri, headers: getHeaders(image.uri) }}
								style={styles.image}
							/>
						) : (
							<VideoItem
								source={{ uri: image.uri, headers: getHeaders(image.uri) }}
								file={image.file}
								deviceType={deviceType}
							/>
						)}
						<TouchableOpacity
							style={styles.removeIcon}
							onPress={() => onRemove(images.indexOf(image))}
						>
							<RemoveIcon opacity={0.8} />
						</TouchableOpacity>
					</TouchableOpacity>
				))}
			</ScrollView>

			<View style={styles.actions}>
				{!disabledNew && <AddNewButton style={buttonStyle} onPress={onAdd} />}
				{actions}
			</View>
		</View>
	);
};

const VideoItem = memo(
	({
		source,
		file,
		deviceType,
	}: {
		source: AVPlaybackSourceObject;
		file?: File;
		deviceType: DeviceType;
	}) => {
		const remoteMediaSource = useMediaSource(source);
		const videoSource = file ? { uri: URL.createObjectURL(file) } : remoteMediaSource;

		const isDesktop = deviceType === DeviceType.DESKTOP;

		return (
			<Video
				source={videoSource}
				style={styles.image}
				videoStyle={styles.image}
				resizeMode={ResizeMode.COVER}
				useNativeControls={!isDesktop}
				isMuted
			/>
		);
	},
);

const styles = StyleSheet.create({
	root: {
		gap: spacing(),
	},
	imagesContainer: {
		gap: spacing(),
	},
	image: {
		width: 230,
		height: 120,
		resizeMode: 'cover',
		borderRadius: 8,
	},
	removeIcon: {
		position: 'absolute',
		top: 16,
		right: 16,
	},
	actions: { flexDirection: 'row', gap: spacing(2) },
});
